import React from 'react';
import moment from 'moment';
import { Dropdown, Menu } from 'antd';
import { useTranslation } from 'react-i18next'
import { noop } from 'lodash';
import { DownOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import classes from 'classnames';
import './style.scss';

/**
 * CommentBlock component - to be used inside CommentModal.
 *
 * @param {Object} props - Props for the CommentBlock component.
 * @param {Boolean} props.isOwner - Indicates if user is owner of comment.
 * @param {string} props.isEdited - 'Y' if edited or 'N' if not edited.
 * @param {function} props.onEdit - Function to be called when editing.
 * @param {function} props.onDelete - Function to be called when deleting.
 * @param {string} props.sender - Text for sender's login.
 * @param {string} props.message - Text for message content.
 * @param {string} props.date - Text for date content.
 * @param {string} props.profile - Text for sender's profile name.
 * @returns {JSX.Element} The CommentBlock component.
 * @example
 *  <CommentBlock
        message={'comment-test'}
        isEdited={'N'}
        sender={'sender-test'}
        profile={'TA'}
        date={'1234-12-12'}
        isOwner={false}
        onEdit={() => randomFunction()}
        onDelete={() => randomFunction()}
    />
 */
function CommentBlock({ isOwner, isEdited, onEdit, onDelete, sender, message, date, profile }) {
    const className = classes('comment-block', {
        'reverse': isOwner
    });

    const { t } = useTranslation()

    const menu = (
        <Menu triggerSubMenuAction='click'>
            <Menu.Item key='0-1' icon={<EditOutlined />} onClick={onEdit}>
                {t('edit')}
            </Menu.Item>
            <Menu.Item key='0-2' icon={<DeleteOutlined />} onClick={onDelete}>
                {t('deleteComment')}
            </Menu.Item>
        </Menu>
    );

    /**
     * Change comment's label color depending on user's profile (ADM, TA, PDM, Leader)
     * @returns {string}
     */
    const changeProfileColor = () => {
        if (['ADM', 'TA', 'PDM', 'Leader', 'BP'].includes(profile)) {
            return profile.toLowerCase();
        } else {
            return 'none';
        }
    };

    return (
        <div className={className} aria-label='comment' role='group'>
            <div className='content-container'>
                {isOwner && (
                    <div className='action-menu-container' aria-label={t('editComment')} role='button' tabIndex={0}>
                        <Dropdown
                            className='actions'
                            trigger={['click']}
                            overlay={menu}
                            placement='bottomRight'
                        >
                            <DownOutlined className='toggle-icon' role='none' />
                        </Dropdown>
                    </div>
                )}

                <div aria-describedby='comment-block' tabIndex={0}>
                    <div className='header'>
                        <div className='header-user'>
                            <span className='sender-name'>{sender}</span>
                            <span className={`sender-profile ${changeProfileColor()}`}>
                                {profile}
                            </span>
                            {isEdited === 'Y' && (
                                <span className="edited-title">
                                    {t('edited')}
                                </span>
                            )}
                        </div>

                        <span className='message-time header-user'>
                            {moment(date).format('DD/MM/YYYY HH:mm')}
                        </span>
                    </div>
                    <div className='message-content'>{message}</div>
                </div>

            </div>
        </div>
    );
}

CommentBlock.defaultProps = {
    isOwner: false,
    isEdited: 'N',
    onEdit: noop,
    onDelete: noop,
    sender: '',
    message: '',
    date: '',
    profile: '',
};

export default CommentBlock;
